import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Skeleton } from '@opendate-app/setlist';

const PublicFolderViewer = ({ folder, team_name, share_access_level, share_token }) => {
  const [currentFolder, setCurrentFolder] = useState(folder);
  const [items, setItems] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: folder.id, name: folder.human_file_name }]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const isRootFolder = currentFolder.id === folder.id;

  const fetchItems = useCallback((folderId) => {
    setLoading(true);
    setError(null);

    const url = folderId === folder.id
      ? `/f/${share_token}/items`
      : `/f/${share_token}/folders/${folderId}/items`;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load folder contents');
        return res.json();
      })
      .then((data) => {
        const sorted = sortItems(data);
        setItems(sorted);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [folder.id, share_token]);

  useEffect(() => {
    fetchItems(folder.id);
  }, [fetchItems, folder.id]);

  const sortItems = (itemList) => {
    return [...itemList].sort((a, b) => {
      const aIsFolder = a.item && a.item.is_folder;
      const bIsFolder = b.item && b.item.is_folder;
      if (aIsFolder && !bIsFolder) return -1;
      if (!aIsFolder && bIsFolder) return 1;
      const aName = (a.item_name || '').toLowerCase();
      const bName = (b.item_name || '').toLowerCase();
      return aName.localeCompare(bName);
    });
  };

  const navigateToFolder = (folderedItem) => {
    const subfolderId = folderedItem.item.id;

    fetch(`/f/${share_token}/folders/${subfolderId}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load folder');
        return res.json();
      })
      .then((data) => {
        setCurrentFolder(data.folder);
        setBreadcrumbs((prev) => [...prev, { id: data.folder.id, name: data.folder.human_file_name }]);
        fetchItems(data.folder.id);
      })
      .catch((err) => {
        setError(err.message);
      });
  };

  const navigateToBreadcrumb = (index) => {
    const target = breadcrumbs[index];
    if (target.id === currentFolder.id) return;

    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);

    if (target.id === folder.id) {
      setCurrentFolder(folder);
      setBreadcrumbs(newBreadcrumbs);
      fetchItems(folder.id);
    } else {
      fetch(`/f/${share_token}/folders/${target.id}`)
        .then((res) => {
          if (!res.ok) throw new Error('Failed to load folder');
          return res.json();
        })
        .then((data) => {
          setCurrentFolder(data.folder);
          setBreadcrumbs(newBreadcrumbs);
          fetchItems(data.folder.id);
        })
        .catch((err) => {
          setError(err.message);
        });
    }
  };

  const handleUpload = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append('files[]', file));

    const url = isRootFolder
      ? `/f/${share_token}/upload`
      : `/f/${share_token}/upload/${currentFolder.id}`;

    fetch(url, { method: 'POST', body: formData })
      .then((res) => {
        if (!res.ok) throw new Error('Upload failed');
        return res.json();
      })
      .then(() => {
        setUploading(false);
        fetchItems(currentFolder.id);
      })
      .catch((err) => {
        setUploading(false);
        setError(err.message);
      });

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const downloadFolderUrl = isRootFolder
    ? `/f/${share_token}/download_folder`
    : `/f/${share_token}/download_folder/${currentFolder.id}`;

  const getItemIcon = (item) => {
    if (item.is_folder) return { className: 'folder-icon', icon: 'fas fa-folder' };
    if (item.is_image) return { className: 'image-icon', icon: 'fas fa-file-image' };
    return { className: 'file-icon', icon: item.fontawesome_icon_class || 'fas fa-file' };
  };

  const renderBreadcrumbs = () => {
    if (breadcrumbs.length <= 1) return null;

    return (
      <div className="pfv-breadcrumbs">
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={crumb.id}>
            {index > 0 && <span className="pfv-breadcrumb-separator"><i className="fas fa-chevron-right" /></span>}
            {index < breadcrumbs.length - 1 ? (
              <a className="pfv-breadcrumb-link" onClick={() => navigateToBreadcrumb(index)}>
                {crumb.name}
              </a>
            ) : (
              <span className="pfv-breadcrumb-current">{crumb.name}</span>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderSkeleton = () => (
    <div>
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="pfv-skeleton-row">
          <Skeleton width={32} height={32} radius={6} />
          <Skeleton width={200} height={14} />
          <Skeleton width={100} height={14} />
          <Skeleton width={60} height={14} />
        </div>
      ))}
    </div>
  );

  const renderEmpty = () => (
    <div className="pfv-empty">
      <div className="pfv-empty-icon"><i className="fas fa-folder-open" /></div>
      <div className="pfv-empty-text">This folder is empty</div>
    </div>
  );

  const renderError = () => (
    <div className="pfv-error">
      <span className="pfv-error-message"><i className="fas fa-exclamation-circle mr-2" />{error}</span>
      <button className="pfv-btn pfv-btn-secondary" onClick={() => fetchItems(currentFolder.id)}>Retry</button>
    </div>
  );

  const renderListView = () => (
    <table className="pfv-table">
      <thead>
        <tr>
          <th>Name</th>
          <th className="pfv-col-date">Modified</th>
          <th className="pfv-col-size">Size</th>
          <th className="pfv-col-type">Type</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {items.map((folderedItem) => {
          const item = folderedItem.item;
          const isFolder = item.is_folder;
          const { className, icon } = getItemIcon(item);

          return (
            <tr key={folderedItem.id}>
              <td>
                <div
                  className={`pfv-col-name ${isFolder ? 'clickable' : ''}`}
                  onClick={isFolder ? () => navigateToFolder(folderedItem) : undefined}
                >
                  <div className={`pfv-item-icon ${className}`}>
                    <i className={icon} />
                  </div>
                  <span className="pfv-item-name">{folderedItem.item_name}</span>
                </div>
              </td>
              <td className="pfv-col-date">{item.human_updated_at}</td>
              <td className="pfv-col-size">{isFolder ? `${item.foldered_items_count} items` : (item.human_file_size || '-')}</td>
              <td className="pfv-col-type">{isFolder ? 'Folder' : (item.human_extension || '-')}</td>
              <td className="pfv-col-actions">
                {isFolder ? (
                  <a className="pfv-download-link" href={`/f/${share_token}/download_folder/${item.id}`}>
                    <i className="fas fa-download mr-1" />Download
                  </a>
                ) : (
                  <a className="pfv-download-link" href={`/f/${share_token}/download/${item.id}`}>
                    <i className="fas fa-download mr-1" />Download
                  </a>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderGridView = () => (
    <div className="pfv-grid">
      {items.map((folderedItem) => {
        const item = folderedItem.item;
        const isFolder = item.is_folder;
        const { className, icon } = getItemIcon(item);
        const showThumbnail = item.is_image && item.compressed_file_url;

        return (
          <div
            key={folderedItem.id}
            className={`pfv-grid-card ${isFolder ? 'clickable' : ''}`}
            onClick={isFolder ? () => navigateToFolder(folderedItem) : undefined}
          >
            {showThumbnail ? (
              <img className="pfv-grid-thumbnail" src={item.compressed_file_url} alt={folderedItem.item_name} />
            ) : (
              <div className={`pfv-grid-icon ${className}`}>
                <i className={icon} />
              </div>
            )}
            <div className="pfv-grid-name">{folderedItem.item_name}</div>
            <div className="pfv-grid-meta">
              {isFolder ? `${item.foldered_items_count} items` : (item.human_file_size || '')}
            </div>
            {!isFolder && (
              <a
                className="pfv-download-link"
                href={`/f/${share_token}/download/${item.id}`}
                onClick={(e) => e.stopPropagation()}
                style={{ fontSize: 12, marginTop: 8, display: 'inline-block' }}
              >
                <i className="fas fa-download mr-1" />Download
              </a>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="pfv-container">
      <div className="pfv-header">
        <h1 className="pfv-folder-name">{currentFolder.human_file_name}</h1>
        <div className="pfv-actions">
          <button
            className={`pfv-btn-icon ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List view"
          >
            <i className="fas fa-list" />
          </button>
          <button
            className={`pfv-btn-icon ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid view"
          >
            <i className="fas fa-th" />
          </button>

          <a className="pfv-btn pfv-btn-secondary" href={downloadFolderUrl}>
            <i className="fas fa-download" />Download All
          </a>

          {share_access_level === 'upload' && (
            <>
              <input
                ref={fileInputRef}
                className="pfv-upload-input"
                type="file"
                multiple
                onChange={handleUpload}
              />
              <button
                className="pfv-btn pfv-btn-primary"
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <span className="pfv-upload-progress">
                    <i className="fas fa-spinner fa-spin" />Uploading...
                  </span>
                ) : (
                  <><i className="fas fa-upload" />Upload</>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {renderBreadcrumbs()}

      {error && renderError()}

      {loading ? renderSkeleton() : (
        items.length === 0 ? renderEmpty() : (
          viewMode === 'list' ? renderListView() : renderGridView()
        )
      )}
    </div>
  );
};

PublicFolderViewer.propTypes = {
  folder: PropTypes.object.isRequired,
  team_name: PropTypes.string.isRequired,
  share_access_level: PropTypes.string.isRequired,
  share_token: PropTypes.string.isRequired,
};

export default PublicFolderViewer;
